/* eslint-disable react/no-deprecated */
import { omit } from 'lodash'
import { get, isFinite, noop } from 'lodash'
import React, { Component, SyntheticEvent } from 'react'
import { Form } from 'react-bootstrap'
import { findDOMNode } from 'react-dom'
import { Tooltip } from 'reactstrap'

import Overlay from '../Overlay'

type TNumberInput = {
  id?: string
  className?: string
  tooltip?: string
  noMargin?: boolean
  hideButtons?: boolean
  value?: number
  block?: boolean
  style?: CSSStyleDeclaration
  short?: boolean
  topLabel?: string
  min?: number
  max?: number
  mustBePositive?: boolean
  autofocus?: boolean
  onFocus?: () => void
  onChange?: (newValue: number) => void
  onBlur?: () => void
  disableKeys?: boolean
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  placeholder?: string
  width?: string
  canBeNull?: boolean
  allowZeroValue?: boolean
  disabled?: boolean
  small?: boolean
}

type TStateNumberInput = {
  error?: any
  value?: number
  showTooltip?: boolean
}

/**
 * Input field that only accepts numbers (int, float). Other inputs are not possible, will be marked as invalid on input, and will be
 * reset on blur. No validation required.
 */
class NumberInput extends Component<TNumberInput, TStateNumberInput> {
  refInput: HTMLInputElement | null = null

  constructor(props: TNumberInput) {
    super(props)
    const { value } = props
    this.state = {
      value,
    }
  }

  componentWillReceiveProps(nextProps: TNumberInput): void {
    this.setState({ value: nextProps.value })
  }

  onFocus = (): void =>
    this.setState({ showTooltip: true }, () => {
      const { autofocus } = this.props
      if (autofocus) {
        // eslint-disable-next-line react/no-find-dom-node
        const node = findDOMNode(this.refInput) as any
        if (node && get(node, 'select')) {
          node.select()
        }
      }
      if (this.props.onFocus) {
        this.props.onFocus()
      }
    })

  onBlur = (): void => {
    const { error, value } = this.state
    const newValue = error ? this.props.value : value
    this.setState(
      {
        error: undefined,
        value: newValue,
        showTooltip: false,
      },
      () => {
        if (this.props.onChange) {
          this.props.onChange(newValue as number)
        }

        if (this.props.onBlur) {
          this.props.onBlur()
        }
      },
    )
  }

  change = (diff: unknown): void => {
    const { value, onChange } = this.props
    const newValue = Number(value) + Number(diff)
    if (!this.validate(newValue)) return
    onChange && onChange(newValue)
    if (this.props.onBlur) {
      this.props.onBlur()
    }
  }

  validate = (v: number): boolean | number => {
    const {
      min = 0,
      max = 99999999,
      mustBePositive,
      canBeNull,
      allowZeroValue,
    } = this.props
    const value = Number(v)

    if (allowZeroValue) {
      return (
        this.isZeroValue(value) ||
        (canBeNull && !v) ||
        (v &&
          isFinite(value) &&
          value >= min &&
          value <= max &&
          (!mustBePositive || value > 0))
      )
    }

    return (
      (canBeNull && !v) ||
      (v &&
        isFinite(value) &&
        value >= min &&
        value <= max &&
        (!mustBePositive || value > 0))
    )
  }

  isZeroValue = (v: unknown): boolean => {
    return v === '' || v === 0 || v === null
  }

  onChange = (e: SyntheticEvent): void => {
    const value = get(e, 'target.value', e) as any
    const preparedValue = value.replace(',', '.')
    const error = !this.validate(preparedValue)

    this.setState(
      {
        value: preparedValue,
        error,
      },
      () => {
        if (!error) {
          // console.log("NumberInput onChange", preparedValue)
          this.props.onChange && this.props.onChange(preparedValue)
        }
      },
    )
  }

  increment = (): void => {
    this.change(1)
  }

  decrement = (): void => {
    this.change(-1)
  }

  onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (this.props.disableKeys) return
    if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
      this.increment()
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
      this.decrement()
    } else {
      if (this.props.onKeyUp) {
        this.props.onKeyUp(e)
      }
    }
  }

  render(): React.ReactElement {
    const {
      onKeyUp = noop,
      id = '',
      className = '',
      tooltip,
      noMargin = false,
      hideButtons = false,
      value: propValue,
      block,
      style = {},
      short = false,
      topLabel,
      placeholder,
      width = '150',
      ...rest
    } = this.props
    const { value, showTooltip, error } = this.state

    const extrastyles = short
      ? {
          width: '80px',
          maxWidth: '80px',
          flex: '0',
        }
      : {}

    const formControlProps = omit(rest, [
      'validate',
      'hideButtons',
      'parentFieldProps',
      'isLoading',
      'onClear',
      'canBeNull',
      'autofocus',
      '',
    ])

    return (
      <div
        className={`text-center form-group mb-${noMargin ? '0' : '2'}`}
        style={{
          display: block ? 'block' : 'inline-block',
          position: 'relative',
        }}
      >
        {this.props.disabled && <Overlay opacity={0.3} />}
        <div style={{ width: hideButtons || block ? '' : `${width}px` }}>
          {topLabel && <label>{topLabel}</label>}

          <div className="input-group">
            {!hideButtons && (
              <div className="input-group-prepend">
                <button
                  type="button"
                  className="btn btn-light waves-effect waves-light"
                  onClick={this.decrement}
                >
                  <i className="far fa-minus" />
                </button>
              </div>
            )}
            {tooltip && (
              <Tooltip
                placement="top"
                isOpen={showTooltip}
                target={this.props.id ? this.props.id : ''}
              >
                {tooltip}
              </Tooltip>
            )}
            <Form.Control
              {...formControlProps}
              type="number"
              size={this.props.small ? 'sm' : 'lg'}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              id={id}
              className={`${className || ''} form-control ${id} ${
                error ? 'is-invalid' : ''
              }`}
              onChange={this.onChange}
              onKeyUp={this.onKeyUp}
              value={value}
              //
              style={{
                ...{
                  fontSize: '1.1em',
                  fontWeight: hideButtons ? 'normal' : 'bold',
                  textAlign: hideButtons ? 'left' : 'center',
                },
                ...style,
                ...extrastyles,
              }}
              ref={(ref) => {
                this.refInput = ref
              }}
              placeholder={placeholder}
            />
            {!hideButtons && (
              <div className="input-group-append">
                <button
                  type="button"
                  className="btn btn-light waves-effect waves-light"
                  onClick={this.increment}
                >
                  <i className="far fa-plus" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
}

export default NumberInput
